/**
 * Date-only helpers for field trips and daily reports.
 *
 * Postgres `date` columns arrive as 'YYYY-MM-DD'. `new Date('YYYY-MM-DD')`
 * parses that as UTC midnight, which renders as the previous day in every
 * timezone west of Greenwich, and `toISOString()` produces the UTC date, so
 * a report filed at 9pm Pacific would be keyed to tomorrow. Everything here
 * works in the technician's local calendar.
 */

import { calendarDaysBetween, localDateString } from '@pazportal/ui';

// The calendar-date parsing rule lives once, in @pazportal/ui (lib/dates.ts).
export { calendarDaysBetween, isDateOnly, localDateString, parseDateOnly } from '@pazportal/ui';

export type TripPhase = 'before' | 'during' | 'after' | 'unknown';

export interface TripDayInfo {
  phase: TripPhase;
  /** 1-based day of the trip for `today`, computed from start_date. Null without a start date. */
  dayNumber: number | null;
  /** Inclusive length of the trip in calendar days. Null without both dates. */
  totalDays: number | null;
  /** Short human label: "Day 3 of 5", "Starts in 2 days", "Ended 4 days ago", "Dates not set". */
  label: string;
}

/**
 * Where `today` falls relative to the trip's start/end dates. Purely
 * arithmetic on the stored dates; nothing here infers progress.
 */
export function tripDayInfo(
  startDate: string | null | undefined,
  endDate: string | null | undefined,
  today: string = localDateString(),
): TripDayInfo {
  const sinceStart = startDate ? calendarDaysBetween(startDate, today) : null;
  const untilEnd = endDate ? calendarDaysBetween(today, endDate) : null;
  const totalDays =
    startDate && endDate ? (() => {
      const span = calendarDaysBetween(startDate, endDate);
      return span === null ? null : Math.max(1, span + 1);
    })() : null;

  if (sinceStart === null && untilEnd === null) {
    return { phase: 'unknown', dayNumber: null, totalDays, label: 'Dates not set' };
  }

  if (sinceStart !== null && sinceStart < 0) {
    const days = -sinceStart;
    return {
      phase: 'before',
      dayNumber: null,
      totalDays,
      label: days === 1 ? 'Starts tomorrow' : `Starts in ${days} days`,
    };
  }

  if (untilEnd !== null && untilEnd < 0) {
    const days = -untilEnd;
    return {
      phase: 'after',
      dayNumber: sinceStart === null ? null : sinceStart + 1,
      totalDays,
      label: days === 1 ? 'Ended yesterday' : `Ended ${days} days ago`,
    };
  }

  const dayNumber = sinceStart === null ? null : sinceStart + 1;
  const label =
    dayNumber === null
      ? untilEnd === 0
        ? 'Ends today'
        : `Ends in ${untilEnd} days`
      : totalDays === null
        ? `Day ${dayNumber}`
        : `Day ${dayNumber} of ${totalDays}`;
  return { phase: 'during', dayNumber, totalDays, label };
}

/**
 * Day number to file today's report under. The stored value wins; otherwise
 * count from start_date, and fall back to the next unused number when the
 * trip has no start date.
 */
export function reportDayNumber(
  existingDayNumber: number | null | undefined,
  startDate: string | null | undefined,
  filedDayNumbers: number[],
  today: string = localDateString(),
): number {
  if (existingDayNumber != null && existingDayNumber > 0) return existingDayNumber;
  const sinceStart = startDate ? calendarDaysBetween(startDate, today) : null;
  if (sinceStart !== null) return Math.max(1, sinceStart + 1);
  const maxFiled = filedDayNumbers.reduce((max, n) => (n > max ? n : max), 0);
  return maxFiled + 1;
}
